<template>
  <div>
    <div class="mobile-container" v-if="selectedQuestions" :key="vacancyQuestionKey">
      <VCard class="grey lighten-5 pa-0 pa-md-6 my-3 my-md-6" elevation="0">
        <h2 v-if="selectedQuestions.length === 0" class="step-title">
          {{ $t('firstQuestion') }}
        </h2>
        <template v-else>
          <h2 class="step-title">
            {{ $t('congrats') }}
          </h2>
          <h4 class="step-subtitle mb-0 mb-md-4">
            {{ $t('anyTime') }}
          </h4>
          <WizardCounts :questions="selectedQuestions.length" class="px-4 px-md-0" />
          <div v-if="selectedQuestions.length > 20">
            <VCard class="many-questions mx-4 mx-md-0 pa-4 mb-4" elevation="0">
              <VIcon large class="mr-3">
                mdi-alert-outline
              </VIcon>
              {{ $t('manyQuestions') }}
            </VCard>
          </div>
          <VacancyQuestionsLibraryDialog
            v-if="$vuetify.breakpoint.smAndDown && selectedQuestions.length > 6"
            :selected="selectedQuestions"
            @update="updateQuestions"
            :visibility="libraryDialog"
            :questions="questions"
            :categories="categories"
            :vacancy-id="vacancy.id"
            :interviewer-id="interviewerId"
          />
        </template>
        <Draggable
          v-model="selectedQuestions"
          class="mb-0 mb-md-6"
          :handle="$vuetify.breakpoint.smAndDown ? '.handle' : false"
        >
          <TransitionGroup>
            <div v-for="(q,index) in selectedQuestions" :key="`${q.type}-${q.id}`">
              <VacancyQuestionRow
                :key="vacancyQuestionKey"
                :question="q"
                :index="index"
                class="draggable-question"
                @updated="updateQuestion"
                @destroy="showDestroyDialog"
              />
            </div>
          </TransitionGroup>
        </Draggable>
        <VacancyQuestionsLibraryDialog
          :selected="selectedQuestions"
          @update="updateQuestions"
          :visibility="libraryDialog"
          :questions="questions"
          :categories="categories"
          :interviewer-id="interviewerId"
          :vacancy-id="vacancy.id"
        />
      </VCard>
      <div v-if="selectedQuestions.length > 20">
        <VCard class="many-questions mx-4 mx-md-0 pa-4 mb-4" elevation="0">
          <VIcon large class="mr-3">
            mdi-alert-outline
          </VIcon>
          {{ $t('manyQuestions') }}
        </VCard>
      </div>
    </div>
    <VacancyQuestionDestroyDialog
      @close="destroyDialog = false"
      v-model="destroyDialog"
      @destroy="destroyQuestion"
    />
    <slot name="buttons" :disabled="submitDisabled" :submit="submit">
      <VBtn
        color="success"
        :dark="!submitDisabled"
        :disabled="submitDisabled"
        :loading="loading"
        elevation="0"
        @click="submit"
      >
        {{ $t('save') }}
      </VBtn>
    </slot>
  </div>
</template>

<script>
import Draggable from 'vuedraggable';
import WizardCounts from '@/components/Vacancy/WizardCounts';
import VacancyQuestionDestroyDialog from '@/components/Vacancy/VacancyQuestionDestroyDialog';
import VacancyQuestionRow from './VacancyQuestionRow.vue';
import VacancyQuestionsLibraryDialog from './VacancyQuestionsLibraryDialog.vue';
import ValidationMixin from '../../mixins/ValidationMixin';

export default {
  name: 'VacancyQuestionsForm',
  components: {
    WizardCounts,
    Draggable,
    VacancyQuestionRow,
    VacancyQuestionsLibraryDialog,
    VacancyQuestionDestroyDialog,
  },
  mixins: [ValidationMixin],
  props: {
    vacancy: {
      type: Object,
      required: true,
    },
    selected: {
      type: Array,
      required: true,
    },
    questions: {
      type: Array,
      required: true,
    },
    categories: {
      type: Array,
      required: true,
    },
    interviewerId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      libraryDialog: false,
      questionToDestroy: null,
      destroyDialog: false,
      selectedQuestions: [...this.selected],
      vacancyQuestionKey: Date.now(),
    };
  },
  computed: {
    submitDisabled() {
      return this.selected.length === 0;
    },
  },
  watch: {
    selected() {
      this.selectedQuestions = this.selected;
    },
  },
  methods: {
    async submit() {
      this.$emit('submit', this.selectedQuestions);
    },
    updateQuestion(question) {
      this.selectedQuestions = this.selectedQuestions
        .map((q) => (q.type !== question.type || q.id !== question.id ? q : question));
      this.submit();
    },
    showDestroyDialog(question) {
      this.questionToDestroy = question;
      this.destroyDialog = true;
    },
    closeDestroyDialog() {
      this.destroyDialog = false;
    },
    updateQuestions(questions) {
      this.selectedQuestions = questions;
      this.submit();
    },
    destroyQuestion() {
      this.selectedQuestions = this.selectedQuestions
        .filter(
          (q) => q.type !== this.questionToDestroy.type || q.id !== this.questionToDestroy.id,
        );
      this.closeDestroyDialog();
      this.submit();
      this.vacancyQuestionKey = Date.now();
    },
  },
};
</script>

<style lang="scss" scoped>
  .draggable-question {
    cursor: all-scroll;
    background-color: transparent;
  }
  .sortable-ghost {
    background-color: #c4c4c4;
  }
  .many-questions {
    display: flex;
    align-items: start;
    font-weight: 300;
    font-size: 14px;
    line-height: 18px;
    background-color: #FFFBD7 !important;
    border: 1px solid #EAE8D7 !important;
  }
  @media #{map-get($display-breakpoints, 'sm-and-down')} {
    .mobile-container {
      width: calc(100% + 24px);
      margin-left: -12px;
    }
  }
</style>

<i18n locale="en" lang="yaml">
  addQuestion: Add question
  save: Save
  firstQuestion: Add your first question
  yes: Yes
  no: No
  congrats: You have completed question selection.
  anyTime: You can delete a question at any time, swap questions or add a new question.
  manyQuestions: You have added an excessive number of questions. The interview will take too long and it will be difficult to check a large number of answers. We recommend cutting down the number of questions.
</i18n>

<i18n locale="ru" lang="yaml">
  addQuestion: Добавить вопрос
  save: Сохранить
  firstQuestion: Добавьте первый вопрос
  yes: Да
  no: Нет
  congrats: Ура! Вы завершили подбор вопросов.
  anyTime: В любой момент вы можете удалить вопрос, поменять вопросы местами или добавить новый.
  manyQuestions: "Вы набрали избыточное количество вопросов. Интервью займет слишком много времени и вам будет сложно проверять большое число ответов. Мы рекомендуем сократить количество вопросов."
</i18n>
